/**
 * 地図ラベルの言語切替（design §11-4 / 要件20）。
 *
 * 新世代 GeoMaps のラベルレイヤーは text-field に
 *   ["coalesce", ["get","name:en"], ["get","name"]]
 * という形で言語別フィールドを参照する（Standard で30層）。日本語表示では内側の
 * `name:en` 参照を `name:ja` 優先の coalesce に差し替える。タイル側に `name:ja` は
 * 実在する（Las Vegas の z14 タイルでも name:en / name:ja / name:zh を確認済み）。
 *
 * 重要: symbol レイヤーには道路番号シールド（`shield_text` / `ref` を描くもの・Standard で26層）や
 * 住居番号（`addr_housenumber`）も含まれる。これらを name で上書きすると道路番号が地名に化けるため、
 * **元の text-field が `name:en` を参照しているレイヤーだけ**を対象にする。
 *
 * glyphs に CJK は含まれない（旧 `maps/v0` も同様）が、MapLibre の localIdeographFontFamily
 * （既定 `sans-serif`）がクライアント側で日本語を描くため表示は従来どおり。
 *
 * 自前で追加したレイヤー（venues / trails）は対象外にする。
 */
import type {
  ExpressionSpecification,
  Map as MaplibreMap,
} from 'maplibre-gl';

const OWN_LAYER_PREFIXES = ['venues', 'trails'];

function isOwnLayer(id: string): boolean {
  return OWN_LAYER_PREFIXES.some((p) => id.startsWith(p));
}

/**
 * スタイルロードごとの「元の text-field」をレイヤー単位で覚えておく。
 *
 * `map.getStyle()` は setLayoutProperty 後の**現在値**を返すため、英語へ戻すときの原型は
 * 自前で保持する必要がある。setStyle でスタイルを差し替えたらこのキャッシュは破棄する。
 */
const originalTextFields = new WeakMap<MaplibreMap, Map<string, unknown>>();

/** スタイル差し替え（setStyle）時に原型キャッシュを捨てる。 */
export function resetMapLabelCache(map: MaplibreMap): void {
  originalTextFields.delete(map);
}

/**
 * 式を再帰的に走査し、`["get","name:en"]` を `name:ja` 優先の coalesce へ置換した複製を返す。
 * `["step", ["zoom"], ["get","name:short"], 4, ["coalesce", ...]]` のような複合式でも
 * 内側の name 参照だけが差し替わる。
 */
function localizeToJa(expr: unknown): unknown {
  if (!Array.isArray(expr)) {
    return expr;
  }
  if (expr.length === 2 && expr[0] === 'get' && expr[1] === 'name:en') {
    return [
      'coalesce',
      ['get', 'name:ja'],
      ['get', 'name:en'],
      ['get', 'name'],
    ];
  }
  return expr.map((item) => localizeToJa(item));
}

/** 地図ラベル（背景スタイル側）の言語を切り替える。 */
export function setMapLabelLanguage(map: MaplibreMap, lang: 'ja' | 'en'): void {
  const style = map.getStyle();
  if (!style?.layers) {
    return;
  }

  let cache = originalTextFields.get(map);
  if (!cache) {
    cache = new Map<string, unknown>();
    originalTextFields.set(map, cache);
  }

  for (const layer of style.layers) {
    if (layer.type !== 'symbol' || isOwnLayer(layer.id)) {
      continue;
    }
    const layout = (layer as { layout?: Record<string, unknown> }).layout;
    if (!layout || !('text-field' in layout)) {
      continue;
    }
    // 原型は初回だけ記録する（2回目以降は現在値ではなくこれを基準にする）。
    if (!cache.has(layer.id)) {
      cache.set(layer.id, layout['text-field']);
    }
    const original = cache.get(layer.id);
    // name 系ラベル以外（道路番号シールド・住居番号など）は触らない。
    if (!JSON.stringify(original ?? null).includes('name:en')) {
      continue;
    }
    const field = (
      lang === 'ja' ? localizeToJa(original) : original
    ) as ExpressionSpecification;
    try {
      map.setLayoutProperty(layer.id, 'text-field', field);
    } catch {
      // 一部レイヤーは上書きできない場合がある。無視して継続。
    }
  }
}
